/**
 * 全データのエクスポート（docs/01-requirements.md §5.14、docs/02-architecture.md §9）。
 *
 * 要件: 「mamemo-6 がなくても本文と画像を読める形式であること」。
 * ZIP 生成は**クライアント側**で行う（Workers 無料枠の CPU 制限を回避。ADR-009）。
 *
 * 生成物:
 *   notes/<subject_date>-<id>.md   YAML frontmatter 付きの Markdown
 *   images/<id>.<ext>              保存済み画像
 *   manifest.json                  補助メタデータ
 *
 * 本文中の画像参照 `/api/images/<id>` は `../images/<id>.<ext>` へ書き換える。
 * これによりエクスポート後のフォルダをそのまま Obsidian 等で開いても画像が表示される。
 */

import { zip } from 'fflate';
import { api } from '@app/api/client';
import type { Category, ImageMeta, Note } from '@shared/types';

export interface ExportProgress {
  phase: 'manifest' | 'notes' | 'images' | 'zip' | 'done';
  loaded: number;
  total: number;
}

const EXT_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/** YAML の文字列値として安全に出す。日本語をそのまま読める形に保つ。 */
function yamlString(value: string): string {
  // 引用符・改行・先頭記号を含む場合のみクォートする。
  if (value === '') return '""';
  if (/^[\w぀-ヿ㐀-鿿][^\n:#]*$/.test(value) && !value.endsWith(' ')) {
    return value;
  }
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
}

function frontmatter(note: Note, categoryName: string | null): string {
  const lines = ['---'];
  lines.push(`id: ${note.id}`);
  if (note.title) lines.push(`title: ${yamlString(note.title)}`);
  lines.push(`subject_date: ${note.subject_date}`);
  if (note.subject_date_end) lines.push(`subject_date_end: ${note.subject_date_end}`);
  if (categoryName) lines.push(`category: ${yamlString(categoryName)}`);
  lines.push(`created_at: ${note.created_at}`);
  lines.push(`updated_at: ${note.updated_at}`);
  if (note.source_note_id) lines.push(`source_note_id: ${note.source_note_id}`);
  lines.push('---', '');
  return lines.join('\n');
}

/** 本文中の `/api/images/<id>` を、ZIP 内の相対パスへ書き換える。 */
export function rewriteImageRefs(body: string, extById: Map<string, string>): string {
  return body.replace(/\/api\/images\/([A-Za-z0-9]+)/g, (whole, id: string) => {
    const ext = extById.get(id);
    return ext ? `../images/${id}.${ext}` : whole;
  });
}

/** ファイル名に使えない文字を落とす。 */
function safeName(value: string): string {
  return value.replace(/[\\/:*?"<>|]/g, '_');
}

export async function exportAllAsZip(
  onProgress?: (progress: ExportProgress) => void,
): Promise<Blob> {
  const report = (phase: ExportProgress['phase'], loaded: number, total: number) =>
    onProgress?.({ phase, loaded, total });

  report('manifest', 0, 1);
  const manifest = await api.exportManifest();
  const categoryById = new Map<number, Category>(manifest.categories.map((c) => [c.id, c]));

  // 画像は本文の書き換えに拡張子が必要なので、先にメタデータだけ集める。
  const files: Record<string, Uint8Array> = {};
  const extById = new Map<string, string>();
  const imageMetas: ImageMeta[] = [];

  report('images', 0, manifest.image_ids.length);
  for (const [index, imageId] of manifest.image_ids.entries()) {
    const res = await fetch(`/api/images/${encodeURIComponent(imageId)}`, {
      credentials: 'same-origin',
    });
    if (!res.ok) continue;
    const mime = res.headers.get('Content-Type')?.split(';')[0]?.trim() ?? 'image/jpeg';
    const ext = EXT_BY_MIME[mime] ?? 'bin';
    extById.set(imageId, ext);
    files[`images/${imageId}.${ext}`] = new Uint8Array(await res.arrayBuffer());
    imageMetas.push({
      id: imageId,
      note_id: null,
      mime,
      width: null,
      height: null,
      bytes: files[`images/${imageId}.${ext}`]!.byteLength,
      created_at: '',
      url: `/api/images/${imageId}`,
    });
    report('images', index + 1, manifest.image_ids.length);
  }

  const encoder = new TextEncoder();
  const notesForManifest: Array<Record<string, unknown>> = [];

  report('notes', 0, manifest.note_ids.length);
  for (const [index, noteId] of manifest.note_ids.entries()) {
    const note = await api.getNote(noteId);
    const categoryName = note.category_id ? (categoryById.get(note.category_id)?.name ?? null) : null;
    const body = rewriteImageRefs(note.body, extById);
    const markdown = frontmatter(note, categoryName) + body + (body.endsWith('\n') ? '' : '\n');
    const filename = `notes/${safeName(note.subject_date)}-${safeName(note.id)}.md`;
    files[filename] = encoder.encode(markdown);
    notesForManifest.push({
      id: note.id,
      file: filename,
      title: note.title,
      category: categoryName,
      subject_date: note.subject_date,
      subject_date_end: note.subject_date_end,
      created_at: note.created_at,
      updated_at: note.updated_at,
      source_note_id: note.source_note_id,
      deleted_at: note.deleted_at,
    });
    report('notes', index + 1, manifest.note_ids.length);
  }

  files['manifest.json'] = encoder.encode(
    JSON.stringify(
      {
        format: 'mamemo-6-export',
        version: 1,
        generated_at: manifest.generated_at,
        categories: manifest.categories,
        notes: notesForManifest,
        images: imageMetas.map((m) => ({
          id: m.id,
          file: `images/${m.id}.${extById.get(m.id) ?? 'bin'}`,
          mime: m.mime,
          bytes: m.bytes,
        })),
      },
      null,
      2,
    ),
  );

  files['README.txt'] = encoder.encode(
    [
      'mamemo-6 エクスポート',
      '',
      'notes/     … ノート本文（YAML frontmatter 付き Markdown）',
      'images/    … 画像。本文からは ../images/<id>.<ext> で参照している',
      'manifest.json … 全ノート・画像の一覧とメタデータ',
      '',
      'mamemo-6 がなくても、Markdown エディタで notes/ を開けば本文と画像を読めます。',
      '',
    ].join('\n'),
  );

  report('zip', 0, 1);
  const zipped = await new Promise<Uint8Array>((resolve, reject) => {
    zip(files, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data)));
  });
  report('done', 1, 1);
  // `zipped` の backing buffer は ArrayBuffer なので Blob へそのまま渡せる。
  return new Blob([zipped as unknown as BlobPart], { type: 'application/zip' });
}

/** 生成した ZIP をダウンロードさせる。 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // revoke を遅らせないと Firefox でダウンロードが始まらないことがある。
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
